import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useTask } from '../contexts/TaskContext';
import TaskItem from './TaskItem';

const CATEGORY_CONFIG = {
  daily: {
    label: '毎日やるタスク',
    icon: '🔄',
    color: 'blue',
    placeholder: 'ルーティンタスクを追加...',
  },
  today: {
    label: '今日やること',
    icon: '📋',
    color: 'green',
    placeholder: '今日のタスクを追加...',
  },
  future: {
    label: '明日以降やること',
    icon: '📅',
    color: 'orange',
    placeholder: '将来のタスクを追加...',
  },
};

// restrictToVerticalAxis modifierをインラインで定義
const verticalOnly = ({ transform }) => ({
  ...transform,
  x: 0,
});

// 日付文字列(YYYY-MM-DD)を M/D 表記に。TaskItem と同じ表示形式。
function formatDateCompact(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  return `${m}/${d}`;
}

// 追加フォームの日付入力の最小日付＝明日（明日以降タスク用）
const TOMORROW_ISO = new Date(Date.now() + 86400000).toISOString().split('T')[0];

export default function TaskColumn({ category, onOpenRecurring }) {
  const { getTasksByCategory, addNewTask, reorderTasks } = useTask();
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState('');
  const config = CATEGORY_CONFIG[category];
  const tasks = getTasksByCategory(category);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleAdd = (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    addNewTask(
      newTitle.trim(),
      category,
      category === 'future' && newDate ? newDate : null
    );
    setNewTitle('');
    setNewDate('');
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tasks.findIndex((t) => t.id === active.id);
    const newIndex = tasks.findIndex((t) => t.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      reorderTasks(category, oldIndex, newIndex);
    }
  };

  const completedCount = tasks.filter((t) => t.completed).length;
  const totalCount = tasks.length;

  return (
    <div className={`task-column column-${config.color}`}>
      <div className="column-header">
        <div className="column-title-row">
          <span className="column-icon">{config.icon}</span>
          <h2 className="column-title">{config.label}</h2>
        </div>
        {totalCount > 0 && (
          <span className="column-count">
            {completedCount}/{totalCount}
          </span>
        )}
      </div>

      {totalCount > 0 && (
        <div className="column-progress">
          <div
            className="column-progress-bar"
            style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }}
          />
        </div>
      )}

      {onOpenRecurring && (
        <button type="button" className="recur-open-btn" onClick={onOpenRecurring}>
          <span className="recur-open-icon" aria-hidden="true">🔄</span>
          定期タスク
        </button>
      )}

      <form className="task-add-form" onSubmit={handleAdd}>
        <input
          type="text"
          className="task-add-input"
          placeholder={config.placeholder}
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
        />
        {category === 'future' && (
          // 日付設定 UI ＝ TaskItem と同じ input 直接化方式。
          // input[type=date] 本体を📅ボタンの見た目に重ねて直接タップさせ、
          // モバイルでも OS ネイティブ日付ピッカーが確実に開く。
          // 値の保持先は Firestore ではなくフォームの local state(newDate)。
          <div className={`task-date-area ${newDate ? 'has-date' : 'no-date'}`}>
            <div className="task-date-trigger">
              {/* 見た目（📅 または 選択日）。pointer-events:none で上の input がタップを受ける。 */}
              <span className="task-date-display" aria-hidden="true">
                {newDate ? formatDateCompact(newDate) : '📅'}
              </span>
              {/* 実体のネイティブ日付入力。実寸(44px)・透明・タップ可能。 */}
              <input
                type="date"
                className="task-date-native"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={TOMORROW_ISO}
                aria-label={newDate ? `追加する日付 ${newDate}（変更）` : '追加するタスクの日付を設定'}
              />
            </div>
            {newDate && (
              <button
                type="button"
                className="task-date-clear"
                onClick={() => setNewDate('')}
                aria-label="日付を解除"
                title="日付を解除"
              >
                ×
              </button>
            )}
          </div>
        )}
        <button type="submit" className="task-add-btn" disabled={!newTitle.trim()}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </form>

      <div className="task-list">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[verticalOnly]}
        >
          <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
            {tasks.map((task) => (
              <TaskItem key={task.id} task={task} />
            ))}
          </SortableContext>
        </DndContext>

        {tasks.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon">
              {category === 'daily' ? '🌅' : category === 'today' ? '✨' : '🌙'}
            </span>
            <p>タスクがありません</p>
          </div>
        )}
      </div>
    </div>
  );
}
